import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, MapPin, ArrowRight, Leaf } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { MaskedLines, Reveal, SectionHeading, LeafSprig } from "@/components/Motion";
import { IMAGES, MENU_CATEGORIES, CONTACT } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const CafeHero = () => (
  <section className="relative overflow-hidden bg-paper pt-36 text-ink" data-testid="cafe-hero">
    <LeafSprig className="pointer-events-none absolute -right-8 top-28 h-72 rotate-[160deg] text-leaf/25" />
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.img
            src="/images/logo-cafe-banner.png"
            alt="Root2Shoot Sustainable Café"
            className="mb-9 h-16 w-auto sm:h-20"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.8 }}
            data-testid="cafe-logo"
          />
          <motion.p
            className="label-mono-ink flex items-center gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.8 }}
          >
            Sustainable Café · Studio House, Stockport
            <span className="h-px w-20 bg-gold-deep/60" aria-hidden="true" />
          </motion.p>
          <MaskedLines
            lines={["A community with"]}
            className="mt-6 font-editorial text-5xl leading-[1.0] tracking-tight text-ink sm:text-6xl lg:text-7xl"
          />
          <MaskedLines
            as="p"
            lines={["a kitchen at its heart"]}
            className="font-display text-4xl italic leading-[1.05] tracking-tight text-gold-deep sm:text-5xl lg:text-6xl"
            delay={0.4}
          />
          <motion.p
            className="mt-8 max-w-lg text-base leading-relaxed text-ink/70 sm:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
          >
            All-day breakfasts, proper baps and bean-to-cup coffee — warm, seasonal and
            plant-forward, with veggie and vegan running through the menu. Open to the
            public, Monday to Friday.
          </motion.p>
          <motion.div
            className="mt-9"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
          >
            <a href="#menu" className="btn-ink" data-testid="cafe-cta-menu">
              View the Menu <ArrowRight size={16} />
            </a>
          </motion.div>
        </div>
        <motion.div
          className="flex flex-col gap-12 sm:flex-row sm:gap-16 lg:col-span-4 lg:col-start-9 lg:flex-col lg:gap-12 lg:pt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: EASE }}
        >
          <div className="border-l-2 border-gold-deep/60 pl-6" data-testid="cafe-hours-card">
            <p className="label-mono-ink flex items-center gap-3"><Clock size={14} /> Opening Hours</p>
            <p className="mt-4 font-serif text-xl text-ink">Monday – Friday</p>
            <p className="mt-1 text-sm text-ink/60">8:00 – 15:00</p>
            <p className="mt-4 font-serif text-xl text-ink">Saturday &amp; Sunday</p>
            <p className="mt-1 text-sm text-ink/60">Closed</p>
          </div>
          <div className="border-l-2 border-gold-deep/60 pl-6" data-testid="cafe-address-card">
            <p className="label-mono-ink flex items-center gap-3"><MapPin size={14} /> Find Us</p>
            <p className="mt-4 font-serif text-xl leading-relaxed text-ink">
              {CONTACT.addressLines[0]}<br />{CONTACT.addressLines[1]}
            </p>
            <p className="mt-1 text-sm text-ink/60">{CONTACT.postcode}</p>
          </div>
        </motion.div>
      </div>
    </div>
    <motion.div
      className="mt-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.05, duration: 1.1, ease: EASE }}
    >
      <img
        src={IMAGES.soupBowl}
        alt="A bowl of spiced tomato soup with greens from the Root2Shoot café kitchen in Stockport"
        className="h-[42vh] w-full object-cover sm:h-[52vh]"
        loading="lazy"
        decoding="async"
        data-testid="cafe-hero-image"
      />
    </motion.div>
  </section>
);

const MenuSection = () => {
  const [active, setActive] = useState(MENU_CATEGORIES[0].id);
  const category = MENU_CATEGORIES.find((c) => c.id === active);

  return (
    <section id="menu" className="scroll-mt-28 border-t border-gold-deep/30 bg-paper py-24 text-ink sm:py-32" data-testid="menu-section">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal className="text-center">
          <img
            src="/images/logo-cafe-banner.png"
            alt=""
            aria-hidden="true"
            className="mx-auto mb-9 h-12 w-auto sm:h-14"
            loading="lazy"
            decoding="async"
          />
          <p className="label-mono-ink flex items-center justify-center gap-4">
            <span className="h-px w-12 bg-gold-deep/60" aria-hidden="true" />
            The Menu
            <span className="h-px w-12 bg-gold-deep/60" aria-hidden="true" />
          </p>
          <h2 className="mt-5 font-editorial text-4xl tracking-tight text-ink sm:text-5xl lg:text-6xl">
            From breakfast <span className="font-display italic text-gold-deep">to lunch</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-ink/70">
            Everything's cooked to order in the kitchen at the heart of our community.{" "}
            <Link to="/sustainability" className="text-gold-deep underline-offset-4 hover:underline" data-testid="menu-sustainability-link">
              Here's how we keep it sustainable.
            </Link>
          </p>
        </Reveal>

        <div className="mt-12 flex flex-wrap justify-center gap-x-10 gap-y-4" role="tablist" aria-label="Menu categories">
          {MENU_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              role="tab"
              aria-selected={active === cat.id}
              onClick={() => setActive(cat.id)}
              data-testid={`menu-tab-${cat.id}`}
              className={`pb-2 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                active === cat.id
                  ? "border-b-2 border-gold-deep text-gold-deep"
                  : "border-b-2 border-transparent text-ink/50 hover:text-ink"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="mt-14"
          >
            {category.note && <p className="text-center font-display text-xl italic text-gold-deep">{category.note}</p>}
            <div className="mt-10 text-left">
              {category.items.map((item, i) => (
                <div key={item.name}>
                  {item.group && (i === 0 || category.items[i - 1].group !== item.group) && (
                    <p className="label-mono-ink mb-2 mt-12 flex items-center gap-4">
                      {item.group}
                      <span className="h-px w-16 bg-gold-deep/60" aria-hidden="true" />
                    </p>
                  )}
                  <div className="border-b border-ink/10 py-7" data-testid={`menu-item-${active}-${i}`}>
                    <div className="flex items-baseline gap-3">
                      <h3 className="font-serif text-2xl tracking-tight text-ink">{item.name}</h3>
                      <span className="mx-1 flex-1 border-b border-dotted border-ink/25" aria-hidden="true" />
                      {item.price && <span className="font-display text-xl italic text-gold-deep">{item.price}</span>}
                    </div>
                    {item.detail && <p className="mt-2 max-w-md text-sm leading-relaxed text-ink/65">{item.detail}</p>}
                    {item.includes && (
                      <ul className="mt-2 max-w-md space-y-1">
                        {item.includes.map((inc) => (
                          <li key={inc} className="flex items-baseline gap-2.5 text-sm leading-relaxed text-ink/65">
                            <span className="h-1 w-1 shrink-0 rotate-45 bg-gold-deep/70" aria-hidden="true" />
                            {inc}
                          </li>
                        ))}
                      </ul>
                    )}
                    {item.dietary?.map((d) => (
                      <p key={d} className="mt-2 flex items-center gap-2 font-display text-base italic text-leaf">
                        <Leaf size={13} className="shrink-0" aria-hidden="true" />
                        {d}
                      </p>
                    ))}
                    {item.choices?.map((c) => (
                      <p key={c.label} className="mt-2 max-w-md text-sm leading-relaxed text-ink/65">
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-deep">{c.label}: </span>
                        {c.options.join("  ·  ")}
                      </p>
                    ))}
                    {item.extras && (
                      <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-ink/50">
                        {item.extras.join("  ·  ")}
                      </p>
                    )}
                    {item.footnote && (
                      <p className="mt-2 font-display text-base italic text-gold-deep/90">{item.footnote}</p>
                    )}
                    {item.tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {item.tags.map((tag) => (
                          <span key={tag} className="border border-leaf/60 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-leaf">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-12 grid gap-10 sm:grid-cols-2">
              <div className="border-t-2 border-leaf/60 pt-6" data-testid="menu-takeaway-note">
                <p className="label-mono-ink flex items-center gap-2">
                  <Leaf size={13} aria-hidden="true" /> Takeaway Options
                </p>
                <p className="mt-4 text-sm leading-relaxed text-ink/70">
                  Takeaway options available. Bring your own reusable cup and enjoy a little
                  discount while helping us reduce single-use waste.
                </p>
              </div>
              <div className="border-t-2 border-leaf/60 pt-6" data-testid="menu-allergen-note">
                <p className="label-mono-ink flex items-center gap-2">
                  <Leaf size={13} aria-hidden="true" /> Dietary &amp; Allergen Information
                </p>
                <p className="mt-4 text-sm leading-relaxed text-ink/70">
                  We do not use nuts in our recipes. However, we cannot guarantee a completely
                  nut-free environment, as some ingredients supplied to us may have been produced in
                  facilities that also handle nuts or may contain traces of nuts.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-ink/70">
                  Please speak to a member of our team about any allergies or dietary requirements
                  before ordering.
                </p>
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">
                  VG = Vegan&nbsp;&nbsp;·&nbsp;&nbsp;GF = Gluten Free&nbsp;&nbsp;·&nbsp;&nbsp;DF = Dairy Free&nbsp;&nbsp;·&nbsp;&nbsp;NF = Nut Free
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

const PhotoStrip = () => (
  <section className="bg-noir py-24 sm:py-32" data-testid="cafe-photos-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading
        kicker="From the Kitchen"
        title={<>Today's prep, <span className="font-display italic text-gold">tomorrow's plate</span></>}
        className="mb-16"
      />
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-12">
        <Reveal className="relative lg:col-span-5">
          <div className="absolute -inset-0 translate-x-3 translate-y-3 border border-gold/40" aria-hidden="true" />
          <img
            src={IMAGES.riceBowl}
            alt="A seasonal rice bowl with soft egg, pickled vegetables and fresh herbs"
            className="relative aspect-[4/5] w-full object-cover"
            loading="lazy"
            decoding="async"
            data-testid="cafe-photo-rice"
          />
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-4 lg:mt-24">
          <img
            src={IMAGES.fruitPlatter}
            alt="A fresh fruit platter prepared in the Root2Shoot kitchen"
            className="aspect-[3/4] w-full object-cover"
            loading="lazy"
            decoding="async"
            data-testid="cafe-photo-fruit"
          />
        </Reveal>
        <Reveal delay={0.2} className="sm:col-span-2 lg:col-span-3 lg:mt-44">
          <img
            src={IMAGES.prepBowls}
            alt="Wooden prep bowls of pickled onions, shredded leeks and fresh herbs in the Root2Shoot kitchen"
            className="aspect-[4/3] w-full object-cover lg:aspect-[3/4]"
            loading="lazy"
            decoding="async"
            data-testid="cafe-photo-prep"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

const PHILOSOPHY = [
  { num: "01", title: "Cooked to order, all day", body: "No heat lamps, no shortcuts. Every breakfast, bap and bowl is made when you ask for it.", testid: "philosophy-fresh" },
  { num: "02", title: "Plants at the centre", body: "Veggie and vegan options aren't an afterthought here — they're half the menu, built by people who live it.", testid: "philosophy-plants" },
  { num: "03", title: "Waste nothing", body: "Surplus becomes staff meals and specials, trimmings become stocks, and the rest feeds the compost loop.", testid: "philosophy-waste" },
];

const FoodPhilosophy = () => (
  <section className="bg-paper py-24 text-ink sm:py-32" data-testid="cafe-philosophy-section">
    <div className="mx-auto max-w-5xl px-5 sm:px-8">
      <SectionHeading
        tone="light"
        kicker="Food Philosophy"
        title={<>How we think <span className="font-display italic text-gold-deep">about food</span></>}
        className="mb-6"
      />
      <div>
        {PHILOSOPHY.map((p, i) => (
          <Reveal key={p.num} delay={i * 0.08}>
            <div className="grid gap-3 border-t border-ink/15 py-9 sm:grid-cols-[70px_1fr_1.2fr] sm:items-baseline sm:gap-8" data-testid={p.testid}>
              <span className="font-display text-3xl italic text-gold-deep">{p.num}</span>
              <h3 className="font-serif text-2xl tracking-tight text-ink">{p.title}</h3>
              <p className="text-sm leading-relaxed text-ink/65 sm:text-base">{p.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const EXPECT = [
  { title: "Open to everyone", body: "A public café, not a members' club. Walk in, sit down, eat well.", testid: "space-open" },
  { title: "Cooked to order", body: "Everything's made when you ask for it — and breakfast runs all day.", testid: "space-fresh" },
  { title: "Vegetarian, Vegan & Allergy Friendly", body: "Meals always available — never an afterthought, right through the menu.", testid: "space-veggie" },
  { title: "Proper coffee", body: "Bean-to-cup, brewed fresh all day. Worth crossing town for.", testid: "space-coffee" },
];

const SpaceSection = () => (
  <section className="bg-obsidian py-24 sm:py-32" data-testid="cafe-space-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHeading
        kicker="Open to the Public"
        title={<>What to expect <span className="font-display italic text-gold">when you walk in</span></>}
        className="mb-16"
      />
      <div className="grid gap-x-12 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
        {EXPECT.map((f, i) => (
          <Reveal key={f.title} delay={i * 0.08}>
            <div className="border-t border-gold/30 pt-7" data-testid={f.testid}>
              <h3 className="font-serif text-xl tracking-tight text-cream">{f.title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-sage">{f.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CafeContact = () => (
  <section className="bg-paper py-24 text-ink sm:py-28" data-testid="cafe-contact-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <div className="border-y border-gold-deep/40 py-16 text-center" data-testid="cafe-contact-banner">
          <p className="label-mono-ink">Contact the Café</p>
          <h2 className="mt-5 font-editorial text-3xl tracking-tight text-ink sm:text-5xl">
            Questions, private hires, <span className="font-display italic text-gold-deep">feedback?</span>
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-base">
            <a href={`mailto:${CONTACT.email}`} className="text-gold-deep underline-offset-4 hover:underline" data-testid="cafe-email">{CONTACT.email}</a>
            <a href={CONTACT.phoneHref} className="text-gold-deep underline-offset-4 hover:underline" data-testid="cafe-phone">{CONTACT.phoneDisplay}</a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer" className="text-gold-deep underline-offset-4 hover:underline" data-testid="cafe-whatsapp">Also on WhatsApp</a>
          </div>
          <p className="mt-10 text-sm text-ink/60">
            Feeding a crew, not just a craving?{" "}
            <Link to="/catering" className="text-gold-deep underline-offset-4 hover:underline" data-testid="cafe-cta-catering">
              See Film &amp; TV Catering
            </Link>
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Cafe() {
  return (
    <>
      <Seo
        title="Sustainable Café in Stockport — All-Day Breakfast & Seasonal Menus | Root2Shoot"
        siteName="Root2Shoot"
        description="Root2Shoot's sustainable café at Studio House, Battersea Road, Stockport — open to the public Monday to Friday, 8:00–15:00. All-day breakfasts, proper baps, plant-forward seasonal food and bean-to-cup coffee, with vegetarian, vegan and allergy-friendly options throughout."
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Sustainable Café", path: "/cafe" }]}
        image="/images/logo-cafe-banner.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CafeOrCoffeeShop",
          name: "Root2Shoot Sustainable Café",
          servesCuisine: "Plant-forward café food",
          priceRange: "£",
          email: CONTACT.email,
          telephone: CONTACT.phoneInternational,
          address: {
            "@type": "PostalAddress",
            streetAddress: CONTACT.street,
            addressLocality: CONTACT.locality,
            addressRegion: CONTACT.region,
            postalCode: CONTACT.postcode,
            addressCountry: "GB",
          },
          openingHoursSpecification: CONTACT.openingHoursSchema,
        }}
      />
      <CafeHero />
      <MenuSection />
      <PhotoStrip />
      <FoodPhilosophy />
      <SpaceSection />
      <CafeContact />
    </>
  );
}
